import { type KeyboardEvent, useId, useMemo, useState } from 'react'

// Choix dans une longue liste (dossiers du plan de classement) : on tape quelques lettres, les
// options qui contiennent tous les mots tapés restent, sans tenir compte des accents ni de la
// casse ; flèches, Entrée et Échap au clavier. La valeur est l'identifiant de l'option.

export type OptionCherchable = { valeur: string; libelle: string }

const normaliser = (t: string) => t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

/** Options dont le libellé contient tous les mots de la recherche, dans l'ordre de la liste. */
export function filtrerOptions<O extends OptionCherchable>(
  options: readonly O[],
  recherche: string,
) {
  const mots = normaliser(recherche).split(/\s+/).filter(Boolean)
  if (mots.length === 0) return [...options]
  return options.filter((o) => {
    const l = normaliser(o.libelle)
    return mots.every((m) => l.includes(m))
  })
}

/** Au plus ce nombre d'options montrées à la fois. */
const MAX_MONTREES = 40

export function ChoixCherchable({
  id,
  options,
  valeur,
  onChange,
  placeholder = 'Taper pour chercher…',
  autoFocus = false,
}: {
  id?: string
  options: readonly OptionCherchable[]
  valeur: string
  onChange: (valeur: string) => void
  placeholder?: string
  autoFocus?: boolean
}) {
  const idListe = useId()
  const choisie = options.find((o) => o.valeur === valeur) ?? null
  const [recherche, setRecherche] = useState<string | null>(null)
  const [actif, setActif] = useState(0)
  const ouverte = recherche !== null
  const trouvees = useMemo(
    () => filtrerOptions(options, recherche ?? '').slice(0, MAX_MONTREES),
    [options, recherche],
  )
  const choisir = (o: OptionCherchable) => {
    onChange(o.valeur)
    setRecherche(null)
  }
  const clavier = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!ouverte) setRecherche('')
      setActif((a) => Math.min(a + 1, trouvees.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActif((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter' && ouverte) {
      // Entrée choisit l'option en surbrillance, sans soumettre le formulaire.
      e.preventDefault()
      const o = trouvees[actif]
      if (o) choisir(o)
    } else if (e.key === 'Escape' && ouverte) {
      e.preventDefault()
      setRecherche(null)
    }
  }
  return (
    <div className="choix-cherchable">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={ouverte}
        aria-controls={idListe}
        aria-autocomplete="list"
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder={choisie ? undefined : placeholder}
        value={ouverte ? recherche : (choisie?.libelle ?? '')}
        onFocus={() => {
          setRecherche('')
          setActif(0)
        }}
        onChange={(e) => {
          setRecherche(e.target.value)
          setActif(0)
        }}
        // Laisse le temps au clic sur une option d'être pris avant de refermer la liste.
        onBlur={() => setTimeout(() => setRecherche(null), 150)}
        onKeyDown={clavier}
      />
      {ouverte && (
        <ul id={idListe} role="listbox" className="choix-cherchable-liste">
          {trouvees.length === 0 && <li className="discret">Aucun résultat.</li>}
          {trouvees.map((o, i) => (
            <li
              key={o.valeur}
              role="option"
              aria-selected={i === actif}
              className={i === actif ? 'actif' : undefined}
              onMouseDown={(e) => {
                e.preventDefault()
                choisir(o)
              }}
              onMouseEnter={() => setActif(i)}
            >
              {o.libelle}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
